import { formatKrw, type PaymentPointSplit, type PointBalanceView, type WebPointChoice } from "@makit/shared";

import { LimitBanner } from "../../components/banners";
import { SettingGroup, SettingRow } from "../../components/settings";
import { pointOptions, type PointOption } from "../../lib/points-pay";

/** `W-26` · `W-26p` 의 **포인트 쓰기 칸**. 잔액이 있는 셀러에게만 선다(부르는 쪽이 `pointsOffered` 로 가른다).
 *
 * ⛔ **처음에는 아무것도 골라져 있지 않다**(목업 `W-26` 캡션). 포인트를 저절로 쓰지 않는다. 셀러가 고른다.
 * ⛔ 금액 직접 적기는 없다. 섞으면 **남은 포인트를 전부** 쓴다(O-89 (가), 2026-10-27).
 *
 * 글자는 목업 `W-26` · `W-26p` 그대로이고, 낱말은 PRD `7.5.9` 용어 표(`포인트` · `남은 포인트` · 칸 이름 `포인트 쓰기`.
 * 2026-10-27 등재)를 따른다.
 *
 * ⭐ **고르기 전 한 줄(법무 `L-F79`-7)** `이 결제로 포인트 ₩X을 모두 씁니다. 그중 ₩Y은 MM-DD까지 환불할 수 있는 포인트입니다.` 는
 * 선택지 줄 아래 · 두 몫 줄 위에 선다(목업 `W-26` 의 `.note`). 숫자는 서버가 결제 준비 응답(`pointUse`)에 SPEND 와 같은 꺼내는
 * 순서로 센 것이고 글자는 `lib/points-pay.ts` `pointUseLine` 이 만든다. 이 칸은 그 줄을 받아 그리기만 한다. */
export function PointChoice({
  total,
  points,
  useLine,
  choice,
  split,
  disabled,
  onChoose,
}: {
  total: number;
  points: PointBalanceView;
  /** 고르기 전 한 줄(`pointUseLine`). 부르는 쪽은 이 줄이 설 때만 칸을 세운다. */
  useLine: string;
  choice: WebPointChoice | null;
  /** 고른 갈래의 두 몫. 아직 안 골랐으면 `null`. */
  split: PaymentPointSplit | null;
  disabled: boolean;
  onChoose: (choice: WebPointChoice) => void;
}) {
  const options = pointOptions(total, points.balance);
  return (
    <div style={{ marginTop: 12 }}>
      <SettingGroup title="포인트 쓰기">
        <SettingRow label="남은 포인트" value={formatKrw(points.balance)} />
        <div role="radiogroup" aria-label="포인트 쓰기">
          {options.map((option) => (
            <OptionRow
              key={option.choice}
              option={option}
              total={total}
              selected={choice === option.choice}
              disabled={disabled || option.split === null}
              onChoose={onChoose}
            />
          ))}
        </div>
        {/* ⛔ 고르기 전에 보여야 하는 줄이라 고름과 무관하게 늘 선다(`L-F79`-7). 두 문장짜리 줄글이라 어절 보존을 걷는다(`.prose`). */}
        <p className="hint prose" style={{ margin: "6px 0" }}>
          {useLine}
        </p>
        {/* 고른 결과를 두 줄로 갈라 적는다(목업 `W-26p`). 끝줄이 결제위젯이 받는 금액이라 `지금 결제할 금액`(작업 전체)과
            결제창의 금액 사이를 잇는다. 결제 수단으로만이면 갈라 적을 것이 없다. */}
        {split && split.pointAmount > 0 ? (
          <>
            <SettingRow label="포인트에서" value={formatKrw(split.pointAmount)} />
            {split.tossAmount > 0 ? (
              <SettingRow label="아래 결제 방법으로" value={formatKrw(split.tossAmount)} tone="total" />
            ) : null}
          </>
        ) : null}
      </SettingGroup>
      {split && split.pointAmount > 0 ? <ReturnBanner mixed={split.tossAmount > 0} /> : null}
    </div>
  );
}

function OptionRow({
  option,
  total,
  selected,
  disabled,
  onChoose,
}: {
  option: PointOption;
  total: number;
  selected: boolean;
  disabled: boolean;
  onChoose: (choice: WebPointChoice) => void;
}) {
  const { sentence, amount } = optionText(option, total);
  return (
    <button
      type="button"
      // 스와치 칸을 뺀 3열(목업 `W-26` 의 인라인 값). 꺼진 줄은 목업처럼 흐리게(`opacity:.5`) 선다.
      style={{ gridTemplateColumns: "16px minmax(0, 1fr) auto", ...(option.split === null ? { opacity: 0.5 } : {}) }}
      className={`optrow${selected ? " on" : ""}`}
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={() => onChoose(option.choice)}
    >
      <span className="rd" aria-hidden="true" />
      <span>{sentence}</span>
      <span className="ov mono">{amount}</span>
    </button>
  );
}

/** 줄의 글자. 목업 `W-26` 그대로다. 포인트로만이 서지 않으면 사유를 같은 줄에 적는다(`포인트가 ₩1,000 모자랍니다`). */
function optionText(option: PointOption, total: number): { sentence: string; amount: string } {
  switch (option.choice) {
    case "POINTS_AND_TOSS":
      return {
        sentence: "포인트 모두 쓰고 나머지는 결제 수단으로",
        amount: option.split
          ? `${formatKrw(option.split.pointAmount)} + ${formatKrw(option.split.tossAmount)}`
          : formatKrw(total),
      };
    case "TOSS_ONLY":
      return { sentence: "결제 수단으로만", amount: formatKrw(total) };
    case "POINTS_ONLY":
      return {
        sentence:
          option.shortBy !== null && option.shortBy > 0
            ? `포인트로만 · 포인트가 ${formatKrw(option.shortBy)} 모자랍니다`
            : "포인트로만",
        amount: formatKrw(total),
      };
  }
}

/** 어디로 돌아가는지 한 줄(목업 `W-26` · `W-26p` 의 `i` 배너, 포인트로만은 앱 `A-26` 과 같은 말).
 *
 * ⛔ 목업 배너의 둘째 줄 「돌아온 포인트는 현금으로 돌려드리지 않습니다」는 **싣지 않았다.** PRD 머리 「2026-10-25 둘째
 * 개정」 결정 8 이 「판정 전에는 공개 문서 · 화면에 환불 불가 문장을 싣지 않는다」고 막고 있고(`13.1` O-90 열림), 목업도
 * `문구 법무 검토 중` 배지를 달았다. 판정이 나면 이 자리에 잇는다. */
function ReturnBanner({ mixed }: { mixed: boolean }) {
  return (
    <div style={{ marginTop: 12 }}>
      <LimitBanner icon="i">
        {mixed ? (
          <span className="l1">포인트로 결제한 몫은 포인트로, 결제 수단으로 결제한 몫은 결제 수단으로 돌아갑니다.</span>
        ) : (
          <>
            <span className="l1">포인트로 결제합니다.</span>
            <span className="l2">시작 전 취소와 못 만든 장 금액은 포인트로 돌아갑니다.</span>
          </>
        )}
      </LimitBanner>
    </div>
  );
}
